const keyBy = require('lodash/keyBy')
const { unitDiscount } = require('../discount/unit-discount')

// Same unit discount as DiscountResolver.resolveOrderDiscount, at this sell price
const lineAmount = (line, pricesBySku) => {
  const price = pricesBySku[`product:${line.sku}`]
  const sellPrice = (price && price.sellprice) || 0
  const discount = unitDiscount(sellPrice, line.discountPercentage, line.discountAmount)
  return (line.quantity || 0) * Math.max(sellPrice - discount, 0)
}

/**
 * Prices each row's lines at the row's `createdAt` and replaces them with their totals.
 * Lines are `{ sku, quantity, discountAmount, discountPercentage }`.
 * @param {Object[]} rows each with a `createdAt` and the line arrays named in `totals`
 * @param {Object<string, string>} totals line array key -> total key, e.g. `{ orderLines: 'deliveryAmount' }`
 * @param {import('./price-pg-adapter').PricePGAdapter} priceAdapter
 * @param {*} client pg pool, client or PGAdapter secured connection; only its .query() is used
 */
async function sumLinesAtCreatedAt (rows, totals, priceAdapter, client) {
  const lineKeys = Object.keys(totals)

  // Prices only vary by reference date, so rows sharing a `created_at` can
  // share one price lookup rather than issuing a query per row.
  const rowsByCreatedAt = new Map()
  for (const row of rows) {
    const referenceDate = new Date(row.createdAt).toISOString()
    const group = rowsByCreatedAt.get(referenceDate)
    if (group) {
      group.push(row)
    } else {
      rowsByCreatedAt.set(referenceDate, [row])
    }
  }

  // No .connect() here: PGAdapter's secured connections refuse it, and a pool or a
  // single client both handle these queries through .query() alone.
  await Promise.all([...rowsByCreatedAt].map(async ([referenceDate, groupedRows]) => {
    const skus = [...new Set(
      groupedRows.flatMap(row => lineKeys.flatMap(key => row[key])).map(line => line.sku).filter(sku => sku != null)
    )]
    const { prices } = await priceAdapter.getPriceList({ skus, referenceDate }, client)
    const pricesBySku = keyBy(prices, 'sku')
    for (const row of groupedRows) {
      for (const key of lineKeys) {
        row[totals[key]] = row[key].reduce((acc, line) => acc + lineAmount(line, pricesBySku), 0)
        delete row[key]
      }
      delete row.createdAt
    }
  }))
  return rows
}

module.exports = {
  sumLinesAtCreatedAt
}
